import { fetchWithTimeout, getCookie, setCookie } from './utils';

// Verificación de si estamos en el navegador o servidor
const isBrowser = typeof window !== 'undefined';

/**
 * Interfaz para el objeto de sesión
 */
export interface SessionInfo {
  sessionId: string;
  userId?: string;
  isAuthenticated: boolean;
}

/**
 * Gestor para manejar la sesión de tracking
 */
export class SessionManager {
  private sessionId: string | null = null;
  private userId: string | null = null;
  private apiUrl: string;
  
  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }
  
  /**
   * Inicializa la sesión obteniendo o creando un sessionId
   */
  async initialize(): Promise<void> {
    // No hacer nada si estamos en SSR
    if (!isBrowser) return;
    
    // Intentar recuperar la sesión de las cookies o localStorage
    this.sessionId = this.getSessionIdFromStorage();
    
    if (!this.sessionId) {
      // Si no existe, solicitar una nueva del servidor
      try {
        this.sessionId = await this.requestNewSessionId();
        this.saveSessionIdToStorage(this.sessionId);
      } catch (error) {
        // Si hay un error de red, generar uno localmente
        console.error('Failed to get session ID from server, generating locally:', error);
        this.sessionId = this.generateLocalSessionId();
        this.saveSessionIdToStorage(this.sessionId);
      }
    }
  }
  
  /**
   * Obtiene el ID de sesión actual
   */
  getSessionId(): string {
    // En caso de SSR, devolver una sesión temporal
    if (!isBrowser || !this.sessionId) {
      return 'ssr-session-id';
    }
    return this.sessionId;
  }
  
  /**
   * Establece el ID de usuario cuando se autentifica
   */
  setUserId(userId: string): void {
    // No hacer nada si estamos en SSR
    if (!isBrowser) return;
    
    this.userId = userId;
  }
  
  /**
   * Borra el ID de usuario cuando se cierra la sesión
   */
  clearUserId(): void {
    // No hacer nada si estamos en SSR
    if (!isBrowser) return;
    
    this.userId = null;
  }
  
  /**
   * Obtiene el ID de usuario si está autenticado
   */
  getUserId(): string | null {
    // En SSR siempre devolver null
    if (!isBrowser) return null;
    
    return this.userId;
  }
  
  /**
   * Verifica si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    // En SSR siempre devolver false
    if (!isBrowser) return false;
    
    return this.userId !== null;
  }
  
  /**
   * Obtiene el objeto de información de sesión
   */
  getSessionInfo(): SessionInfo {
    // En SSR, devolver información por defecto
    if (!isBrowser) {
      return {
        sessionId: 'ssr-session-id',
        isAuthenticated: false
      };
    }
    
    return {
      sessionId: this.sessionId || '',
      userId: this.userId || undefined,
      isAuthenticated: this.isAuthenticated()
    };
  }
  
  /**
   * Vincula una sesión anónima a un usuario autenticado
   */
  async linkSessionToUser(userId: string): Promise<void> {
    // No hacer nada si estamos en SSR
    if (!isBrowser) return;
    
    if (!this.sessionId) {
      throw new Error('Session not initialized. Call initialize() first.');
    }
    
    try {
      const response = await fetchWithTimeout(`${this.apiUrl}/tracking/link-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: this.sessionId,
          userId
        }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to link session: ${response.statusText}`);
      }
      
      this.userId = userId;
      
    } catch (error) {
      console.error('Error linking session to user:', error);
      throw error;
    }
  }
  
  /**
   * Obtiene el sessionId de localStorage o cookies
   */
  private getSessionIdFromStorage(): string | null {
    // No hacer nada si estamos en SSR
    if (!isBrowser) return null;
    
    // Primero intentar obtener de localStorage
    try {
    const sessionId = localStorage.getItem('tracking_session_id');
    if (sessionId) {
      return sessionId;
      }
    } catch (error) {
      console.warn('Failed to access localStorage:', error);
    }
    
    // Si no está en localStorage, intentar obtener de las cookies
    return getCookie('sessionId');
  }
  
  /**
   * Guarda el sessionId en localStorage y como cookie
   */
  private saveSessionIdToStorage(sessionId: string): void {
    // No hacer nada si estamos en SSR
    if (!isBrowser) return;
    
    // Guardar en localStorage
    try {
    localStorage.setItem('tracking_session_id', sessionId);
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
    
    // También guardar como cookie de respaldo (90 días)
    setCookie('sessionId', sessionId, 90);
  }
  
  /**
   * Solicita un nuevo sessionId al servidor
   */
  private async requestNewSessionId(): Promise<string> {
    // No hacer nada si estamos en SSR
    if (!isBrowser) return 'ssr-generated-id';
    
    try {
    const response = await fetchWithTimeout(`${this.apiUrl}/tracking/session`, {
      method: 'GET',
      credentials: 'include' // Incluir cookies
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get session ID: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.data.sessionId;
    } catch (error) {
      console.error('Error requesting session ID:', error);
      throw error;
    }
  }
  
  /**
   * Genera un ID de sesión localmente en caso de fallo del servidor
   */
  private generateLocalSessionId(): string {
    return 'local_' + Math.random().toString(36).substring(2, 9) + 
           '_' + Date.now().toString(36);
  }
} 